import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import CarCard from '../components/CarCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, ChevronRight, Shield, Database, Globe,
  Sparkles, ArrowRight, Zap, BookOpen, Car, Filter, X
} from 'lucide-react';
import { useTranslation } from '../LanguageContext';

const HomePage = () => {
  const { t } = useTranslation();
  const [featuredCars, setFeaturedCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [vinInput, setVinInput] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Filter state
  const [filterBrand, setFilterBrand] = useState('');
  const [filterBodyType, setFilterBodyType] = useState('');
  const [filterPriceRange, setFilterPriceRange] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-data`).catch(() => {});
        const response = await axios.get(`${API}/cars/featured`);
        setFeaturedCars(response.data);
        setFilteredCars(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...featuredCars];
    if (filterBrand) {
      result = result.filter(car => car.brand.toLowerCase().includes(filterBrand.toLowerCase()));
    }
    if (filterBodyType) {
      result = result.filter(car => car.body_type.toLowerCase() === filterBodyType.toLowerCase());
    }
    if (filterPriceRange) {
      const [min, max] = filterPriceRange.split('-').map(Number);
      result = result.filter(car => car.price >= min && (max ? car.price <= max : true));
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(car =>
        car.title.toLowerCase().includes(q) ||
        car.brand.toLowerCase().includes(q) ||
        car.model.toLowerCase().includes(q) ||
        car.location.toLowerCase().includes(q)
      );
    }
    setFilteredCars(result);
  }, [filterBrand, filterBodyType, filterPriceRange, filterSearch, featuredCars]);

  const handleVinCheck = (e) => {
    e.preventDefault();
    if (vinInput.trim()) {
      navigate(`/vin-check?vin=${vinInput.trim()}`);
    }
  };

  const clearFilters = () => {
    setFilterBrand('');
    setFilterBodyType('');
    setFilterPriceRange('');
    setFilterSearch('');
  };

  const hasActiveFilters = filterBrand || filterBodyType || filterPriceRange || filterSearch;

  const brands = [...new Set(featuredCars.map(c => c.brand))];
  const bodyTypes = [...new Set(featuredCars.map(c => c.body_type))];

  const features = [
    { icon: Shield, title: t('features.ai'), description: t('features.aiDesc') },
    { icon: Database, title: t('features.data'), description: t('features.dataDesc') },
    { icon: Globe, title: t('features.eu'), description: t('features.euDesc') }
  ];

  const steps = [
    { step: 1, title: t('how.s1'), description: t('how.s1d') },
    { step: 2, title: t('how.s2'), description: t('how.s2d') },
    { step: 3, title: t('how.s3'), description: t('how.s3d') }
  ];

  return (
    <div className="min-h-screen">
      {/* Compact Hero Section */}
      <section className="relative py-6 md:py-8">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
            {/* Left side */}
            <div className="flex-1 text-center lg:text-left">
              <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-500 font-bold mb-2">
                {t('hero.badge')}
              </p>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter uppercase mb-3 leading-tight">
                <span className="text-cyan-500 neon-text">{t('hero.title1')}</span> {t('hero.title2')}{' '}
                <span className="text-cyan-500 neon-text">{t('hero.title3')}</span>
              </h1>
              <p className="text-sm text-gray-400 max-w-md mx-auto lg:mx-0 mb-4">
                {t('hero.subtitle')}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-2 mb-4">
                <Link to="/cars" data-testid="browse-cars-btn">
                  <Button className="h-10 px-5 bg-cyan-500 text-black hover:bg-cyan-400 font-bold uppercase tracking-widest text-xs shadow-neon hover:shadow-neon-lg transition-all">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    {t('hero.explore')}
                    <ChevronRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
                <Link to="/sell" data-testid="sell-car-btn">
                  <Button variant="outline" className="h-10 px-5 border-cyan-500/50 text-cyan-500 hover:bg-cyan-500/10 font-bold uppercase tracking-widest text-xs">
                    {t('hero.sellYour')}
                  </Button>
                </Link>
              </div>

              {/* VIN Check */}
              <form onSubmit={handleVinCheck} className="flex gap-2 max-w-sm mx-auto lg:mx-0">
                <Input
                  type="text"
                  placeholder={t('hero.vinPlaceholder')}
                  value={vinInput}
                  onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                  maxLength={17}
                  className="h-9 bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-cyan-500 font-mono text-xs tracking-wider"
                  data-testid="vin-input"
                />
                <Button type="submit" className="h-9 px-3 bg-cyan-500 text-black hover:bg-cyan-400 font-bold shadow-neon" data-testid="vin-check-btn">
                  <Search className="w-3.5 h-3.5" />
                </Button>
              </form>
            </div>

            {/* Right side - Hero Image (smaller) */}
            <div className="flex-1 max-w-md lg:max-w-lg">
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-r from-cyan-500/15 via-transparent to-cyan-500/15 blur-3xl"></div>
                <img
                  src="https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=700&q=75"
                  alt="Featured Car"
                  className="relative w-full h-auto rounded-xl car-image-stylized border border-white/10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOP AI RECOMMENDATIONS with Filters */}
      <section className="py-8 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Zap className="w-4 h-4 text-cyan-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold uppercase">
                  {t('rec.title1')} <span className="text-cyan-500 neon-text">{t('rec.title2')}</span> {t('rec.title3')}
                </h2>
                <p className="text-xs text-gray-500">{t('rec.subtitle')}</p>
              </div>
            </div>
            <Link to="/cars" className="hidden md:flex items-center gap-1.5 text-cyan-500 hover:text-cyan-400 transition-colors text-sm font-medium">
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Filter Bar */}
          <div className="mb-5 p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-2 mb-2 sm:mb-0">
              <div className="flex items-center gap-1.5 text-gray-400 shrink-0">
                <Filter className="w-3.5 h-3.5 text-cyan-500" />
                <span className="text-xs font-medium">Filters:</span>
              </div>
              <span className="text-[10px] text-gray-600 ml-auto">
                {filteredCars.length} car{filteredCars.length !== 1 ? 's' : ''}
              </span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="flex items-center gap-1 h-7 px-2 text-[11px] text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors shrink-0">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {/* Search */}
              <div className="relative shrink-0 w-[140px] sm:w-[180px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  className="w-full h-8 pl-7 pr-2 text-[11px] bg-white/5 border border-white/10 rounded-md text-white placeholder:text-gray-600 focus:border-cyan-500/50 outline-none"
                  data-testid="rec-search"
                />
              </div>

              {/* Brand */}
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                className="h-8 px-2 text-[11px] bg-white/5 border border-white/10 rounded-md text-gray-300 focus:border-cyan-500/50 outline-none cursor-pointer appearance-none shrink-0 min-w-[90px]"
                data-testid="rec-brand-filter"
              >
                <option value="" className="bg-[#0a0a0a]">All Brands</option>
                {brands.map(b => <option key={b} value={b} className="bg-[#0a0a0a]">{b}</option>)}
              </select>

              {/* Body Type */}
              <select
                value={filterBodyType}
                onChange={(e) => setFilterBodyType(e.target.value)}
                className="h-8 px-2 text-[11px] bg-white/5 border border-white/10 rounded-md text-gray-300 focus:border-cyan-500/50 outline-none cursor-pointer appearance-none shrink-0 min-w-[90px]"
                data-testid="rec-body-filter"
              >
                <option value="" className="bg-[#0a0a0a]">All Types</option>
                {bodyTypes.map(b => <option key={b} value={b} className="bg-[#0a0a0a]">{b}</option>)}
              </select>

              {/* Price Range */}
              <select
                value={filterPriceRange}
                onChange={(e) => setFilterPriceRange(e.target.value)}
                className="h-8 px-2 text-[11px] bg-white/5 border border-white/10 rounded-md text-gray-300 focus:border-cyan-500/50 outline-none cursor-pointer appearance-none shrink-0 min-w-[100px]"
                data-testid="rec-price-filter"
              >
                <option value="" className="bg-[#0a0a0a]">Any Price</option>
                <option value="0-50000" className="bg-[#0a0a0a]">Under &euro;50K</option>
                <option value="50000-100000" className="bg-[#0a0a0a]">&euro;50K - &euro;100K</option>
                <option value="100000-200000" className="bg-[#0a0a0a]">&euro;100K - &euro;200K</option>
                <option value="200000-9999999" className="bg-[#0a0a0a]">&euro;200K+</option>
              </select>
            </div>
          </div>

          {/* Car Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-xl bg-white/5 aspect-[4/5] animate-pulse"></div>
              ))}
            </div>
          ) : filteredCars.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="featured-cars-grid">
              {filteredCars.slice(0, 8).map((car) => (
                <CarCard key={car.id} car={car} featured={car.is_featured} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Car className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No cars match your filters.</p>
              <button onClick={clearFilters} className="text-cyan-500 text-sm mt-2 hover:underline">Clear filters</button>
            </div>
          )}

          <div className="mt-6 text-center md:hidden">
            <Link to="/cars">
              <Button variant="outline" className="border-cyan-500/50 text-cyan-500 text-xs">
                View All Cars <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold uppercase">
              {t('features.title1')} <span className="text-cyan-500 neon-text">{t('features.title2')}</span>{t('features.title3')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div key={index} className="glass-card rounded-xl p-5 hover:border-cyan-500/30 transition-all duration-300 group">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-3 group-hover:bg-cyan-500/20 transition-colors">
                  <feature.icon className="w-5 h-5 text-cyan-500" />
                </div>
                <h3 className="font-heading text-base font-semibold mb-1.5">{feature.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-10 relative bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold uppercase">
              {t('how.title1')} <span className="text-cyan-500 neon-text">{t('how.title2')}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {steps.map((item, index) => (
              <div key={index} className="relative">
                <div className="glass-card rounded-xl p-5 h-full">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-black font-bold text-sm mb-3">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-base font-semibold mb-1.5">{item.title}</h3>
                  <p className="text-gray-400 text-xs">{item.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2.5 w-5 h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wiki CTA */}
      <section className="py-10 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card rounded-2xl p-5 md:p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-60 h-60 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-5">
              <div className="max-w-lg text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <BookOpen className="w-5 h-5 text-cyan-500" />
                  <h2 className="font-heading text-xl md:text-2xl font-bold uppercase">
                    Car <span className="text-cyan-500 neon-text">Wiki</span>
                  </h2>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  Explore our comprehensive database of every car model ever made.
                </p>
                <Link to="/wiki" data-testid="explore-wiki-btn">
                  <Button className="h-9 px-5 bg-cyan-500 text-black hover:bg-cyan-400 font-bold uppercase tracking-widest text-xs shadow-neon">
                    Explore Wiki <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {['BMW', 'Mercedes', 'Audi', 'Tesla', 'Porsche', 'Ferrari'].map((brand) => (
                  <Link
                    key={brand}
                    to={`/wiki/${brand}`}
                    className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all text-[10px] font-medium"
                  >
                    {brand.slice(0, 3)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
